import React, { useState, useRef, useMemo, useCallback, useEffect } from "react";
import { Icon, Button } from "semantic-ui-react";
import { toast } from "react-toastify";
import { BasicDrawer, EvidenceTextButton } from "../../../Shared";
import { ActionPlanForm } from "../../ActionPlans/ActionPlanForm";
import { CanvasModal } from "./CanvasModal";
import { Inspection, ChecklistNormReference } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { useSite } from "../../../../contexts";
import { NORM_COUNTRIES } from "../normCountries";
import "./ChecklistExecution.scss";
import { saveOfflineDraft, deleteOfflineDraft, getOfflineDraft } from "../../../../utils/inspectionOffline";

const inspCtrl    = new Inspection();
const normRefCtrl = new ChecklistNormReference();

// ── Helpers ───────────────────────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { key: "compliant",     label: "Cumple",     icon: "check",  color: "#27AE60" },
  { key: "non_compliant", label: "No cumple",  icon: "times",  color: "#C0392B" },
  { key: "na",            label: "N/A",        icon: "minus",  color: "#95A5A6" },
];

const NOTIFY_OPTIONS = [
  "Responsable del área",
  "Supervisor de planta",
  "Coordinador EHS",
  "Mantenimiento",
];

// Ítems aplicables = respondidos con resultado ≠ "na"
// Sin ítems aplicables → 0 (nunca NaN/null)
function calcCumplimiento(responses, items) {
  const applicable = items.filter((i) => {
    const s = responses[i._id]?.status;
    return s && s !== "na";
  });
  if (!applicable.length) return 0;
  const score = applicable.reduce((acc, i) => {
    const s = responses[i._id]?.status;
    return s === "compliant" ? acc + 1 : acc;
  }, 0);
  return Math.round((score / applicable.length) * 100 * 10) / 10;
}

function groupByCategory(items) {
  const groups = {};
  items.forEach((item) => {
    const cat = item.category || "General";
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(item);
  });
  return groups;
}

// Detecta viewport angosto (mobile) para el modo "1 ítem por pantalla" — es
// comportamiento (autoavance), no solo estilo, por eso hace falta en JS y no
// alcanza con un @media de CSS.
function useIsNarrowViewport(breakpoint = 640) {
  const [isNarrow, setIsNarrow] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= breakpoint
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const handler = (e) => setIsNarrow(e.matches);
    setIsNarrow(mq.matches);
    if (mq.addEventListener) mq.addEventListener("change", handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else mq.removeListener(handler);
    };
  }, [breakpoint]);
  return isNarrow;
}

// ── Botón de evidencia ────────────────────────────────────────────────────────

function EvidenceButton({ icon, label, count, active, onClick }) {
  return (
    <button
      className={`cle__ev-btn${active ? " cle__ev-btn--active" : ""}`}
      onClick={onClick}
      type="button"
    >
      <Icon name={icon} />
      {label}
      {count > 0 && <span className="cle__ev-count">{count}</span>}
    </button>
  );
}

// ── Fila de ítem ──────────────────────────────────────────────────────────────

function ItemRow({ item, response, inspectionId, onUpdate, onOpenCapa, derivationMode }) {
  const { accessToken } = useAuth();
  const photoInputRef  = useRef(null);
  const fileInputRef   = useRef(null);
  const photoUrlsRef   = useRef({}); // { [key]: objectURL } — espejo síncrono de photoUrls, para revoke en cleanup

  const [showComment,  setShowComment]  = useState(false);
  const [showCanvas,   setShowCanvas]   = useState(false);
  const [showNotify,   setShowNotify]   = useState(false);
  const [uploading,    setUploading]    = useState(false);
  const [photoUrls,    setPhotoUrls]    = useState({}); // { [key]: objectURL } — fotos requieren fetch autenticado

  const status   = response?.status   || null;
  const photos   = response?.photos   || [];
  const files    = response?.files    || [];
  const comment  = response?.comment  || "";
  const annotation = response?.annotation || null;
  const alertTo  = response?.alertTo  || "";

  const needsCapa = status === "non_compliant";

  const upd = (patch) => onUpdate(item._id, { ...response, ...patch });

  // El endpoint /inspection-file requiere Authorization header, así que las
  // fotos no pueden usarse directo como src de <img> — se traen como blob
  // autenticado y se convierten en object URL.
  useEffect(() => {
    let cancelled = false;
    photos.forEach((p) => {
      if (photoUrlsRef.current[p.key]) return;
      inspCtrl.getFileBlob(accessToken, p.key).then((blob) => {
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        photoUrlsRef.current[p.key] = url;
        setPhotoUrls((prev) => ({ ...prev, [p.key]: url }));
      }).catch(() => {});
    });
    return () => { cancelled = true; };
  }, [photos, accessToken]);

  useEffect(() => {
    return () => {
      Object.values(photoUrlsRef.current).forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  async function openFile(key) {
    // Sin "noopener": necesitamos la referencia de la ventana para setearle
    // la blob URL después del fetch (con "noopener" el navegador devuelve
    // null y la pestaña queda en blanco para siempre).
    const win = window.open("", "_blank");
    try {
      const blob = await inspCtrl.getFileBlob(accessToken, key);
      const url = URL.createObjectURL(blob);
      if (win) win.location.href = url;
    } catch {
      if (win) win.close();
      toast.error("No se pudo abrir el archivo.");
    }
  }

  async function handlePhotos(e) {
    const selected = Array.from(e.target.files);
    if (!selected.length) return;
    setUploading(true);
    try {
      const uploaded = await Promise.all(
        selected.map((f) => inspCtrl.uploadFile(accessToken, inspectionId, f))
      );
      upd({ photos: [...photos, ...uploaded] });
    } catch {
      toast.error("Error al subir fotos.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  }

  async function handleFiles(e) {
    const selected = Array.from(e.target.files);
    if (!selected.length) return;
    setUploading(true);
    try {
      const uploaded = await Promise.all(
        selected.map((f) => inspCtrl.uploadFile(accessToken, inspectionId, f))
      );
      upd({ files: [...files, ...uploaded] });
    } catch {
      toast.error("Error al subir archivos.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  }

  function removePhoto(idx) {
    upd({ photos: photos.filter((_, i) => i !== idx) });
  }

  return (
    <div className={`cle__item${status ? ` cle__item--${status}` : ""}`}>
      {/* Pregunta */}
      <div className="cle__question">
        {item.required !== false && <span className="cle__required">*</span>}
        <span>{item.question}</span>
        {item.helpText && (
          <span className="cle__helptext" title={item.helpText}>
            <Icon name="info circle" />
          </span>
        )}
      </div>

      {/* Botones de estado */}
      <div className="cle__status-row">
        {STATUS_OPTIONS.map((opt) => (
          <button
            key={opt.key}
            type="button"
            className={`cle__status-btn${status === opt.key ? " cle__status-btn--active" : ""}`}
            style={status === opt.key ? { background: opt.color, borderColor: opt.color, color: "#fff" } : {}}
            onClick={() => upd({ status: opt.key })}
          >
            <Icon name={opt.icon} />
            {opt.label}
          </button>
        ))}
      </div>

      {/* Barra de evidencia (sólo si respondió) */}
      {status && (
        <div className="cle__evidence-bar">
          <EvidenceButton
            icon="camera"
            label="Foto"
            count={photos.length}
            active={photos.length > 0}
            onClick={() => photoInputRef.current?.click()}
          />
          <EvidenceButton
            icon="comment outline"
            label="Comentario"
            count={comment ? 1 : 0}
            active={showComment || !!comment}
            onClick={() => setShowComment((v) => !v)}
          />
          <EvidenceButton
            icon="attach"
            label="Adjunto"
            count={files.length}
            active={files.length > 0}
            onClick={() => fileInputRef.current?.click()}
          />
          <EvidenceButton
            icon="bell outline"
            label="Notificar"
            count={alertTo ? 1 : 0}
            active={showNotify || !!alertTo}
            onClick={() => setShowNotify((v) => !v)}
          />
          {uploading && <Icon name="circle notch" loading />}

          {/* Inputs ocultos */}
          <input
            ref={photoInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            multiple
            style={{ display: "none" }}
            onChange={handlePhotos}
          />
          <input
            ref={fileInputRef}
            type="file"
            multiple
            style={{ display: "none" }}
            onChange={handleFiles}
          />
        </div>
      )}

      {/* Comentario expandible */}
      {status && showComment && (
        <textarea
          className="cle__comment"
          rows={2}
          placeholder="Observación del ítem..."
          value={comment}
          onChange={(e) => upd({ comment: e.target.value })}
        />
      )}

      {/* Notificar a expandible */}
      {status && (showNotify || alertTo) && (
        <div className="cle__notify">
          <Icon name="bell outline" />
          <span className="cle__notify-label">Notificar a:</span>
          <select
            className="cle__notify-select"
            value={alertTo}
            onChange={(e) => upd({ alertTo: e.target.value })}
          >
            <option value="">Sin notificar</option>
            {NOTIFY_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
      )}

      {/* Thumbnails de fotos */}
      {photos.length > 0 && (
        <div className="cle__thumbs">
          {photos.map((p, i) => (
            <div key={i} className="cle__thumb">
              <img
                src={photoUrls[p.key] || ""}
                alt={p.name}
                title={p.name}
                onClick={() => photoUrls[p.key] && window.open(photoUrls[p.key], "_blank")}
              />
              <button
                className="cle__thumb-remove"
                type="button"
                onClick={() => removePhoto(i)}
              >×</button>
            </div>
          ))}
        </div>
      )}

      {/* Anotación guardada */}
      {annotation && (
        <div className="cle__annotation-preview">
          <img src={annotation} alt="Anotación" onClick={() => setShowCanvas(true)} title="Clic para editar" />
          <button
            type="button"
            className="cle__annotation-remove"
            onClick={() => upd({ annotation: null })}
          >×</button>
        </div>
      )}

      {/* Archivos adjuntos */}
      {files.length > 0 && (
        <div className="cle__files">
          {files.map((f, i) => (
            <span key={i} className="cle__file-pill">
              <a href="#" onClick={(e) => { e.preventDefault(); openFile(f.key); }} title={f.name}>
                <Icon name="file outline" />{f.name}
              </a>
              <EvidenceTextButton module="inspection" fileKey={f.key} fileName={f.name} style={{ width: 20, height: 20 }} />
              <button type="button" onClick={() => upd({ files: files.filter((_, j) => j !== i) })}>×</button>
            </span>
          ))}
        </div>
      )}

      {/* Foto obligatoria (requirePhotoOnNonCompliant) */}
      {item.requirePhotoOnNonCompliant && status === "non_compliant" && photos.length === 0 && (
        <div className="cle__photo-required">
          <Icon name="camera" />
          Foto requerida como evidencia de "No cumple"
        </div>
      )}

      {/* Toggle CAPA (solo en modo manual) */}
      {needsCapa && derivationMode !== "auto" && (
        <div className="cle__capa-row">
          <button
            type="button"
            className={`cle__capa-btn${response?.generateCapa ? " cle__capa-btn--on" : ""}`}
            onClick={() => {
              const next = !response?.generateCapa;
              upd({ generateCapa: next });
              if (next) onOpenCapa(item, response);
            }}
          >
            <Icon name="lightning" />
            {response?.generateCapa ? "Acción correctiva vinculada" : "Generar acción correctiva"}
          </button>
          {response?.accionId && (
            <span className="cle__capa-id">AP-{response.accionId.slice(-6).toUpperCase()}</span>
          )}
        </div>
      )}
      {/* Modo AUTO: informativo */}
      {needsCapa && derivationMode === "auto" && (
        <div className="cle__capa-row cle__capa-row--auto">
          <Icon name="bolt" />
          Acción correctiva se generará automáticamente al finalizar
        </div>
      )}

      {/* Canvas modal */}
      <CanvasModal
        show={showCanvas}
        onClose={() => setShowCanvas(false)}
        onSave={(dataUrl) => upd({ annotation: dataUrl })}
        initialImage={annotation}
      />
    </div>
  );
}

// ── Modal de revisión antes de firmar ────────────────────────────────────────

function countAnswers(items, responses) {
  let compliant = 0, nonCompliant = 0, na = 0;
  items.forEach((item) => {
    const s = responses[item._id]?.status;
    if (s === "compliant") compliant++;
    else if (s === "non_compliant") nonCompliant++;
    else if (s === "na") na++;
  });
  return { compliant, nonCompliant, na };
}

// Fila de un hallazgo dentro de Revisión — si ya existe guardado en la base
// (persisted, con _id real) se puede cerrar acá mismo con foto "después" +
// responsable. Si es un hallazgo recién detectado en esta misma sesión
// (todavía sin guardar), no tiene _id todavía — no hay nada que cerrar hasta
// que se guarde al menos una vez.
function ReviewFindingRow({ item, response, persisted, onClose, closing }) {
  const [showCloseForm, setShowCloseForm] = useState(false);
  const [fixPhoto,   setFixPhoto]   = useState(null);
  const [fixByName,  setFixByName]  = useState("");
  const hasPhoto = (response?.photos || []).length > 0;
  const isClosed = persisted?.status === "closed";

  const handleSubmit = async () => {
    if (!fixPhoto) { toast.warning("Subí la foto de \"después\"."); return; }
    if (!fixByName.trim()) { toast.warning("Ingresá el responsable del cierre."); return; }
    await onClose(persisted._id, fixPhoto, fixByName.trim());
    setShowCloseForm(false);
    setFixPhoto(null);
    setFixByName("");
  };

  return (
    <div className="cle__review-finding">
      <span className="cle__review-finding-cat">{item.category || "General"}</span>
      <span className="cle__review-finding-q">{item.question}</span>

      {!hasPhoto && (
        <span className="cle__review-finding-warn">
          <Icon name="warning sign" /> sin foto
        </span>
      )}

      {!persisted && (
        <span className="cle__review-finding-note">Se guarda al completar / guardar la inspección</span>
      )}

      {isClosed && (
        <span className="cle__review-finding-closed">
          <Icon name="check circle" /> Cerrado{persisted.fixByName ? ` · ${persisted.fixByName}` : ""}
        </span>
      )}

      {persisted && !isClosed && !showCloseForm && (
        <button type="button" className="cle__review-finding-close-btn" onClick={() => setShowCloseForm(true)}>
          <Icon name="camera" /> Cerrar hallazgo
        </button>
      )}

      {persisted && !isClosed && showCloseForm && (
        <div className="cle__review-finding-closeform">
          <input
            type="file"
            accept="image/*"
            capture="environment"
            onChange={(e) => setFixPhoto(e.target.files?.[0] || null)}
          />
          <input
            type="text"
            placeholder="Responsable del cierre"
            value={fixByName}
            onChange={(e) => setFixByName(e.target.value)}
          />
          <div className="cle__review-finding-closeform-actions">
            <Button basic size="mini" type="button" onClick={() => setShowCloseForm(false)} disabled={closing}>
              Cancelar
            </Button>
            <Button color="green" size="mini" type="button" onClick={handleSubmit} loading={closing} disabled={closing}>
              Confirmar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function ReviewModal({ items, responses, cumplimiento, hallazgosPersisted, onCloseHallazgo, onBack, onContinue }) {
  const counts = useMemo(() => countAnswers(items, responses), [items, responses]);
  const [closingId, setClosingId] = useState(null);

  const hallazgos = useMemo(
    () => items.filter((item) => responses[item._id]?.status === "non_compliant"),
    [items, responses]
  );

  const handleClose = async (hallazgoId, file, fixByName) => {
    setClosingId(hallazgoId);
    try {
      await onCloseHallazgo(hallazgoId, file, fixByName);
    } finally {
      setClosingId(null);
    }
  };

  return (
    <div className="cle__review-overlay" onClick={onBack}>
      <div className="cle__review-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cle__review-header">
          <Icon name="check circle" color="green" />
          <span>Revisión antes de firmar</span>
          <button type="button" className="cle__review-close" onClick={onBack}>
            <Icon name="close" />
          </button>
        </div>

        <div className="cle__review-kpis">
          <div className="cle__review-kpi cle__review-kpi--green">
            <span className="cle__review-kpi-value">{counts.compliant}</span>
            <span className="cle__review-kpi-label">Conformes (Sí)</span>
          </div>
          <div className="cle__review-kpi cle__review-kpi--red">
            <span className="cle__review-kpi-value">{counts.nonCompliant}</span>
            <span className="cle__review-kpi-label">Hallazgos (No)</span>
          </div>
          <div className="cle__review-kpi">
            <span className="cle__review-kpi-value">{counts.na}</span>
            <span className="cle__review-kpi-label">No aplica</span>
          </div>
          <div className="cle__review-kpi cle__review-kpi--compliance">
            <span className="cle__review-kpi-value">{cumplimiento}%</span>
            <span className="cle__review-kpi-label">Cumplimiento</span>
          </div>
        </div>

        {hallazgos.length > 0 && (
          <div className="cle__review-findings">
            <div className="cle__review-findings-title">
              Hallazgos a corregir ({hallazgos.length})
            </div>
            <div className="cle__review-findings-list">
              {hallazgos.map((item) => {
                const persisted = hallazgosPersisted?.find((h) => String(h.itemId) === String(item._id));
                return (
                  <ReviewFindingRow
                    key={item._id}
                    item={item}
                    response={responses[item._id]}
                    persisted={persisted}
                    onClose={handleClose}
                    closing={!!persisted && closingId === persisted._id}
                  />
                );
              })}
            </div>
          </div>
        )}

        <div className="cle__review-footer">
          <Button basic type="button" onClick={onBack}>Volver a la lista</Button>
          <Button color="green" type="button" onClick={onContinue}>
            <Icon name="pencil" /> Continuar a firma
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Modal de firma ────────────────────────────────────────────────────────────

function SignatureModal({ items, responses, cumplimiento, defaultName, onCancel, onSign }) {
  const canvasRef = useRef(null);
  const drawing    = useRef(false);
  const lastPos    = useRef(null);

  const [name,          setName]          = useState(defaultName || "");
  const [role,          setRole]          = useState("");
  const [hasSignature,  setHasSignature]  = useState(false);

  const counts = useMemo(() => countAnswers(items, responses), [items, responses]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }, []);

  function getPos(e, canvas) {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width  / rect.width;
    const scaleY = canvas.height / rect.height;
    const src = e.touches ? e.touches[0] : e;
    return {
      x: (src.clientX - rect.left) * scaleX,
      y: (src.clientY - rect.top)  * scaleY,
    };
  }

  function startDraw(e) {
    drawing.current = true;
    lastPos.current = getPos(e, canvasRef.current);
    e.preventDefault();
  }

  function draw(e) {
    if (!drawing.current) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx    = canvas.getContext("2d");
    const pos    = getPos(e, canvas);
    ctx.strokeStyle = "#1A1C42";
    ctx.lineWidth   = 2.5;
    ctx.lineCap     = "round";
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    lastPos.current = pos;
    setHasSignature(true);
  }

  function endDraw() { drawing.current = false; lastPos.current = null; }

  function clearSignature() {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  }

  function handleSign() {
    if (!name.trim()) {
      toast.warning("Ingresá el nombre y apellido de quien ejecuta.");
      return;
    }
    if (!hasSignature) {
      toast.warning("Falta dibujar la firma.");
      return;
    }
    onSign({
      name:  name.trim(),
      role:  role.trim(),
      image: canvasRef.current.toDataURL("image/png"),
    });
  }

  return (
    <div className="cle__review-overlay" onClick={onCancel}>
      <div className="cle__review-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cle__review-header">
          <Icon name="pencil" color="green" />
          <span>Firma del inspector</span>
          <button type="button" className="cle__review-close" onClick={onCancel}>
            <Icon name="close" />
          </button>
        </div>

        <div className="cle__signature-kpis">
          <div className="cle__signature-kpi">
            <span className="cle__signature-kpi-value">{counts.compliant}</span>
            <span className="cle__signature-kpi-label">Sí</span>
          </div>
          <div className="cle__signature-kpi">
            <span className="cle__signature-kpi-value">{counts.nonCompliant}</span>
            <span className="cle__signature-kpi-label">No</span>
          </div>
          <div className="cle__signature-kpi">
            <span className="cle__signature-kpi-value">{counts.na}</span>
            <span className="cle__signature-kpi-label">N/A</span>
          </div>
          <div className="cle__signature-kpi cle__signature-kpi--green">
            <span className="cle__signature-kpi-value">{cumplimiento}%</span>
            <span className="cle__signature-kpi-label">Cumplimiento</span>
          </div>
        </div>

        <div className="cle__signature-body">
          <label className="cle__signature-label">Nombre y apellido de quien ejecuta</label>
          <input
            className="cle__signature-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Nombre y apellido"
          />

          <label className="cle__signature-label">Cargo / Rol</label>
          <input
            className="cle__signature-input"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            placeholder="Cargo / rol"
          />

          <label className="cle__signature-label">Firma</label>
          <canvas
            ref={canvasRef}
            width={560}
            height={150}
            className="cle__signature-canvas"
            onMouseDown={startDraw}
            onMouseMove={draw}
            onMouseUp={endDraw}
            onMouseLeave={endDraw}
            onTouchStart={startDraw}
            onTouchMove={draw}
            onTouchEnd={endDraw}
          />
          <button type="button" className="cle__signature-clear" onClick={clearSignature}>
            Borrar firma
          </button>

          <div className="cle__signature-hint">
            <Icon name="info circle" />
            Al firmar se cierra el registro y se habilita el reporte imprimible. Los hallazgos
            "No" se derivan al Plan de Acción según el modo configurado.
          </div>
        </div>

        <div className="cle__review-footer">
          <Button basic type="button" onClick={onCancel}>Cancelar</Button>
          <Button color="green" type="button" onClick={handleSign}>
            <Icon name="check" /> Firmar y cerrar
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Componente principal ──────────────────────────────────────────────────────

export function ChecklistExecution({
  inspection, template, close, onComplete, geolocation, autoSave = false,
  country: countryProp, onCountryChange,
}) {
  const { accessToken, user } = useAuth();
  const { site: activeSite } = useSite();
  const [normRefs, setNormRefs] = useState([]);
  const templateId = template?._id;

  // Sin `onCountryChange` (ej. flujo standalone del QR, sin Checklist.js
  // arriba manejando el estado) el selector usa un estado local propio, para
  // que igual sea funcional en vez de quedar inerte.
  const [localCountry, setLocalCountry] = useState(NORM_COUNTRIES[0].value);
  const country = countryProp ?? localCountry;
  const handleCountryChange = onCountryChange || setLocalCountry;

  useEffect(() => {
    if (!templateId) { setNormRefs([]); return; }
    normRefCtrl.getNormReferences(accessToken, { template: templateId })
      .then((res) => setNormRefs(Array.isArray(res) ? res : []))
      .catch(() => setNormRefs([]));
  }, [accessToken, templateId]);

  const activeNorm = normRefs.find((n) => n.country === country && n.active !== false);

  const items    = template?.items || [];
  const [responses, setResponses] = useState(() => {
    const init = {};
    (inspection?.responses || []).forEach((r) => {
      init[r.itemId] = {
        status:   r.status,
        comment:  r.comment || "",
        photos:   (r.photos || []).map((key) => ({ key })),
        // Formato legado: string plano (sin nombre). Formato nuevo: {key, name}.
        files:    (r.files || []).map((f) => (typeof f === "string" ? { key: f, name: f } : f)),
        annotation: r.annotation || "",
        alertTo:  r.alertTo || "",
      };
    });
    return init;
  });
  const [saving,    setSaving]    = useState(false);
  const [showReview,    setShowReview]    = useState(false);
  const [showSignature, setShowSignature] = useState(false);

  // Cierre de hallazgos ya persistidos (con _id real, de una sesión previa)
  // — se resuelve acá mismo, dentro del flujo de completar/revisar, no en
  // una pantalla aparte. `hallazgoOverrides` refleja en el momento lo que se
  // va cerrando en esta sesión, sin necesitar recargar toda la inspección.
  const [hallazgoOverrides, setHallazgoOverrides] = useState({});
  const hallazgosPersisted = (inspection.hallazgos || []).map((h) => ({
    ...h,
    ...(hallazgoOverrides[h._id] || {}),
  }));

  const handleCloseHallazgo = async (hallazgoId, file, fixByName) => {
    try {
      const uploaded = await inspCtrl.uploadFile(accessToken, inspection._id, file);
      await inspCtrl.closeHallazgo(accessToken, inspection._id, hallazgoId, {
        photos: [uploaded.key],
        fixByName,
      });
      setHallazgoOverrides((prev) => ({
        ...prev,
        [hallazgoId]: { status: "closed", fixPhotos: [uploaded.key], fixDate: new Date().toISOString(), fixByName },
      }));
      toast.success("Hallazgo cerrado.");
    } catch (err) {
      toast.error(err?.msg || "Error al cerrar el hallazgo.");
    }
  };

  // CAPA drawer
  const [showCapa,    setShowCapa]    = useState(false);
  const [capaPrefill, setCapaPrefill] = useState(null);
  const [capaItemId,  setCapaItemId]  = useState(null);

  const updateResponse = useCallback((itemId, data) => {
    setResponses((prev) => ({ ...prev, [itemId]: data }));
  }, []);

  const openCapa = useCallback((item, resp) => {
    setCapaItemId(item._id);
    setCapaPrefill({
      findingOrigin:      "plant_inspection",
      moduloOrigen:       "Check List",
      title:              item.question,
      findingDescription: [item.question, resp?.comment].filter(Boolean).join(" — "),
      findingType:        "non_conformity",
      priority:           "high",
      inspectionId:       inspection._id,
    });
    setShowCapa(true);
  }, [inspection._id]);

  // Progreso
  const groups = useMemo(() => groupByCategory(items), [items]);
  const answeredCount = useMemo(
    () => items.filter((i) => responses[i._id]?.status).length,
    [items, responses]
  );
  const totalRequired = items.filter((i) => i.required !== false).length;
  const unansweredRequired = items.filter(
    (i) => i.required !== false && !responses[i._id]?.status
  );
  const progress = items.length ? Math.round((answeredCount / items.length) * 100) : 0;
  const cumplimiento = calcCumplimiento(responses, items);

  // ── Modo mobile: 1 ítem por pantalla, con autoavance ──────────────────────
  // Sí / N.A. avanzan solos (respuesta rápida, sin evidencia que documentar).
  // "No" NO avanza — espera a que el inspector cargue foto/comentario antes
  // de seguir, tal como pide la regla.
  const isMobile = useIsNarrowViewport(640);
  const [mobileIndex, setMobileIndex] = useState(0);
  const currentMobileItem = isMobile ? items[mobileIndex] : null;
  const currentMobileStatus = currentMobileItem ? responses[currentMobileItem._id]?.status : null;
  const mobileEvalRef = useRef({ index: -1, status: null });

  useEffect(() => {
    if (!isMobile || !currentMobileItem) return;
    const last = mobileEvalRef.current;
    if (last.index !== mobileIndex) {
      // Llegamos a un ítem nuevo (navegación manual o autoavance) — solo
      // actualiza la referencia, no dispara autoavance por esto.
      mobileEvalRef.current = { index: mobileIndex, status: currentMobileStatus };
      return;
    }
    if (currentMobileStatus && currentMobileStatus !== last.status && currentMobileStatus !== "non_compliant") {
      mobileEvalRef.current = { index: mobileIndex, status: currentMobileStatus };
      const t = setTimeout(() => {
        setMobileIndex((i) => Math.min(i + 1, items.length - 1));
      }, 450);
      return () => clearTimeout(t);
    }
    mobileEvalRef.current = { index: mobileIndex, status: currentMobileStatus };
  }, [mobileIndex, currentMobileStatus, isMobile, currentMobileItem, items.length]);

  // ── Offline / auto-save ───────────────────────────────────────────────────
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const autoSaveRunner            = useRef(null);

  // Sync pending offline draft when network reconnects
  useEffect(() => {
    const goOnline = async () => {
      setIsOffline(false);
      const draft = await getOfflineDraft(inspection._id).catch(() => null);
      if (!draft) return;
      try {
        await inspCtrl.updateInspection(accessToken, inspection._id, draft.payload);
        await deleteOfflineDraft(inspection._id);
      } catch { /* silent — next auto-save will retry */ }
    };
    const goOffline = () => setIsOffline(true);
    window.addEventListener("online",  goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online",  goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, [accessToken, inspection._id]);

  // Trigger auto-save every 60s when autoSave prop is true
  useEffect(() => {
    if (!autoSave) return;
    const id = setInterval(async () => {
      try { await autoSaveRunner.current?.(); } catch { /* silent */ }
    }, 60_000);
    return () => clearInterval(id);
  }, [autoSave]);

  function buildPayload(status) {
    return {
      status,
      cumplimiento,  // nunca null: 0 cuando no hay ítems aplicables
      // La fecha real de la inspección la fija el inspector al completarla
      // (la de creación es solo un placeholder que pide el modelo).
      ...(status === "completed" ? { date: new Date().toISOString() } : {}),
      ...(geolocation ? { geolocation } : {}),
      responses: items.map((item) => {
        const r = responses[item._id] || {};
        return {
          itemId:   item._id,
          question: item.question,
          category: item.category,
          status:   r.status || "na",
          comment:  r.comment || "",
          photos:   (r.photos || []).map((p) => p.key),
          // Se guarda {key, name} para no perder el nombre original del
          // archivo — antes solo se persistía la key generada por el server.
          files:    (r.files  || []).map((p) => ({ key: p.key, name: p.name || p.key })),
          annotation: r.annotation || "",
          alertTo:  r.alertTo || "",
        };
      }),
      // Hallazgo = todo ítem con resultado "No cumple" o "Parcial", independientemente del toggle
      hallazgos: items
        .filter((item) => {
          const r = responses[item._id];
          return r?.status === "non_compliant";
        })
        .map((item) => {
          const r = responses[item._id];
          return {
            itemId:      item._id,
            category:    item.category || "General",
            description: [item.question, r?.comment].filter(Boolean).join(" — "),
            itemStatus:  r.status,
            status:      "open",
            actionPlanId: r?.accionId || undefined,
          };
        }),
    };
  }

  // Update the auto-save runner every render so it always has the latest closure
  autoSaveRunner.current = async () => {
    if (saving || inspection.status !== "draft") return;
    const payload = buildPayload("draft");
    if (navigator.onLine) {
      await inspCtrl.updateInspection(accessToken, inspection._id, payload);
      await deleteOfflineDraft(inspection._id).catch(() => {});
    } else {
      await saveOfflineDraft(inspection._id, payload);
    }
  };

  // Autoguardado "con cada respuesta": debounced (1.2s de inactividad) para
  // no pegarle al server en cada tecla de un comentario, pero ya no depende
  // del timer de 60s (que antes solo corría en el flujo QR/mobile — acá
  // corre siempre, web incluido, que antes no autoguardaba nada).
  const isFirstResponsesRenderRef = useRef(true);
  useEffect(() => {
    if (isFirstResponsesRenderRef.current) { isFirstResponsesRenderRef.current = false; return; }
    if (inspection.status !== "draft") return;
    const timer = setTimeout(() => {
      autoSaveRunner.current?.().catch(() => {});
    }, 1200);
    return () => clearTimeout(timer);
  }, [responses]);

  // El sitio activo (selector global del header) cambia mientras esta
  // inspección sigue abierta: confirma (guarda) el progreso antes de que seas
  // llevado a datos de otro sitio, en vez de perderlo silenciosamente.
  const lastSiteRef = useRef(activeSite);
  useEffect(() => {
    if (activeSite === lastSiteRef.current) return;
    lastSiteRef.current = activeSite;
    if (inspection.status !== "draft") return;
    (async () => {
      try {
        await autoSaveRunner.current?.();
        toast.info("Cambiaste de sitio — tu progreso quedó guardado como borrador.");
      } catch {
        toast.error("No se pudo guardar el borrador al cambiar de sitio.");
      }
    })();
  }, [activeSite]);

  function openReview() {
    if (!navigator.onLine) {
      toast.warning("Necesitás conexión para completar la inspección.");
      return;
    }
    if (unansweredRequired.length > 0) {
      toast.warning(`Faltan ${unansweredRequired.length} ítem(s) obligatorio(s) sin respuesta.`);
      return;
    }
    setShowReview(true);
  }

  async function complete(signature) {
    if (!navigator.onLine) {
      toast.warning("Necesitás conexión para completar la inspección.");
      return;
    }
    if (unansweredRequired.length > 0) {
      toast.warning(`Faltan ${unansweredRequired.length} ítem(s) obligatorio(s) sin respuesta.`);
      return;
    }
    // Evidencia obligatoria: ítems con requirePhotoOnNonCompliant y sin foto
    const missingPhoto = items.filter((item) => {
      const r = responses[item._id];
      return item.requirePhotoOnNonCompliant && r?.status === "non_compliant" && !r?.photos?.length;
    });
    if (missingPhoto.length > 0) {
      toast.warning(`${missingPhoto.length} ítem(s) requieren foto como evidencia de "No cumple".`);
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload("completed");
      if (signature) payload.signature = { ...signature, signedAt: new Date().toISOString() };
      await inspCtrl.updateInspection(accessToken, inspection._id, payload);
      toast.success("Inspección completada.");
      if (onComplete) onComplete();
      else close();
    } catch {
      toast.error("Error al completar la inspección.");
    } finally {
      setSaving(false);
    }
  }

  const inspDate = inspection?.date
    ? new Date(inspection.date).toLocaleDateString("es-AR", { day: "2-digit", month: "short", year: "numeric" })
    : "";

  return (
    <div className="cle">
      {/* Header */}
      <div className="cle-drawer-header">
        <div className="cle-drawer-header__left">
          <span className="cle-drawer-header__id-pill">
            INSP-{inspection._id?.slice(-6).toUpperCase()}
          </span>
          <div>
            <div className="cle-drawer-header__title">{template?.name}</div>
            <div className="cle-drawer-header__subtitle">{inspDate}</div>
          </div>
        </div>

        {/* Progress */}
        <div className="cle-drawer-header__progress">
          <span className="cle-drawer-header__progress-label">
            {answeredCount}/{items.length} respondidos · {cumplimiento !== null ? `${cumplimiento}%` : "N/D"} cumplimiento
          </span>
          <div className="cle-drawer-header__bar">
            <div
              className="cle-drawer-header__bar-fill"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <Button
          className="cle-drawer-header__review-btn"
          color="green"
          type="button"
          onClick={openReview}
          disabled={saving}
        >
          <Icon name="check circle" />
          Revisar y finalizar
        </Button>

        <button className="cle-drawer-header__close" onClick={close} type="button">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      {/* Norma de referencia */}
      <div className="cle__norm-banner">
        <Icon name="shield" />
        <div className="cle__norm-banner-text">
          <span className="cle__norm-banner-label">Norma de referencia</span>
          <span className="cle__norm-banner-value">
            {activeNorm ? activeNorm.norm : "Sin norma cargada para esta plantilla/país"}
          </span>
        </div>
        <div className="cle__norm-banner-country">
          <span>País</span>
          <select
            value={country}
            onChange={(e) => handleCountryChange(e.target.value)}
          >
            {NORM_COUNTRIES.map((c) => (
              <option key={c.value} value={c.value}>{c.text}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Offline banner */}
      {isOffline && (
        <div className="cle__offline-banner">
          <Icon name="wifi" /> Sin conexión — los cambios se guardan localmente
        </div>
      )}

      {/* Contenido scrollable */}
      <div className="cle__scroll">
        {isMobile ? (
          currentMobileItem && (
            <div className="cle__mobile-single">
              <div className="cle__mobile-nav">
                <button
                  type="button"
                  onClick={() => setMobileIndex((i) => Math.max(0, i - 1))}
                  disabled={mobileIndex === 0}
                >
                  <Icon name="chevron left" /> Anterior
                </button>
                <span className="cle__mobile-nav-count">{mobileIndex + 1} / {items.length}</span>
                <button
                  type="button"
                  onClick={() => setMobileIndex((i) => Math.min(items.length - 1, i + 1))}
                  disabled={mobileIndex === items.length - 1}
                >
                  Siguiente <Icon name="chevron right" />
                </button>
              </div>
              <div className="cle__category-header">
                <Icon name="tag" />
                {currentMobileItem.category || "General"}
              </div>
              <div className="cle__items">
                <ItemRow
                  key={currentMobileItem._id}
                  item={currentMobileItem}
                  response={responses[currentMobileItem._id]}
                  inspectionId={inspection._id}
                  onUpdate={updateResponse}
                  onOpenCapa={openCapa}
                  derivationMode={template?.derivationMode || "manual"}
                />
              </div>
            </div>
          )
        ) : (
          Object.entries(groups).map(([category, catItems]) => (
            <div key={category} className="cle__category">
              <div className="cle__category-header">
                <Icon name="tag" />
                {category}
                <span className="cle__category-count">{catItems.length} ítems</span>
              </div>
              <div className="cle__items">
                {catItems.map((item, idx) => (
                  <ItemRow
                    key={item._id || idx}
                    item={item}
                    response={responses[item._id]}
                    inspectionId={inspection._id}
                    onUpdate={updateResponse}
                    onOpenCapa={openCapa}
                    derivationMode={template?.derivationMode || "manual"}
                  />
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal: revisión antes de firmar */}
      {showReview && (
        <ReviewModal
          items={items}
          responses={responses}
          cumplimiento={cumplimiento}
          hallazgosPersisted={hallazgosPersisted}
          onCloseHallazgo={handleCloseHallazgo}
          onBack={() => setShowReview(false)}
          onContinue={() => { setShowReview(false); setShowSignature(true); }}
        />
      )}

      {/* Modal: firma del inspector */}
      {showSignature && (
        <SignatureModal
          items={items}
          responses={responses}
          cumplimiento={cumplimiento}
          defaultName={[user?.firstname, user?.lastname].filter(Boolean).join(" ")}
          onCancel={() => { setShowSignature(false); setShowReview(true); }}
          onSign={(signature) => { setShowSignature(false); complete(signature); }}
        />
      )}

      {/* Drawer CAPA */}
      <BasicDrawer
        show={showCapa}
        close={() => setShowCapa(false)}
        width="70vw"
        hideHeader
        flush
      >
        {showCapa && (
          <ActionPlanForm
            show={showCapa}
            close={() => setShowCapa(false)}
            prefill={capaPrefill}
            onSaved={(ap) => {
              const apId = ap?.actionPlan?._id || ap?._id;
              if (apId && capaItemId) {
                setResponses((prev) => ({
                  ...prev,
                  [capaItemId]: {
                    ...prev[capaItemId],
                    accionId:     String(apId),
                    generateCapa: true,
                  },
                }));
              }
              setCapaItemId(null);
              setShowCapa(false);
              toast.success("Acción correctiva creada y vinculada.");
            }}
          />
        )}
      </BasicDrawer>
    </div>
  );
}
